import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { LoadMoreButton } from '@sorare/core/src/atoms/buttons/LoadMoreButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { extractConnectionData } from '@sorare/core/src/gql/extractData';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { MyPage } from '../MyPage';
import { MySorareSubTabsLayout } from '../MySorareSubTabsLayout';
import { Subscriptions } from '../common/Subscriptions';
import { useSortByDate } from '../common/useSortByDate';
import { useSportSelect } from '../common/useSportSelect';
import { useSubscribableTypesSelect } from '../common/useSubscribableTypesSelect';
import type {
  CurrentUserFollowsQuery,
  CurrentUserFollowsQueryVariables,
} from './__generated__/index.graphql';

const CURRENT_USER_FOLLOWS_QUERY = gql`
  query CurrentUserFollowsQuery(
    $cursor: String
    $types: [SubscribableType!]
    $sortType: SortingOption
  ) {
    currentUser {
      slug
      mySubscriptions(
        types: $types
        after: $cursor
        sortType: $sortType
        first: 50
      ) {
        nodes {
          slug
          ...Subscriptions_subscription
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${Subscriptions.fragments.subscription}
` as TypedDocumentNode<
  CurrentUserFollowsQuery,
  CurrentUserFollowsQueryVariables
>;

const Content = styled(Vertical)`
  padding: var(--double-unit) 0;
`;

export const MyFollows = () => {
  const { sportInputValue, sportSelectNode } = useSportSelect();
  const { sortAsVariable, sortSelectNode } = useSortByDate();
  const { selectedSubscribableTypesVariables, subscribableTypeSelectNode } =
    useSubscribableTypesSelect({ sportInputValue });

  const { data, loading, loadMore } = usePaginatedQuery(
    CURRENT_USER_FOLLOWS_QUERY,
    {
      variables: {
        types: selectedSubscribableTypesVariables,
        sortType: sortAsVariable,
      },
      connection: 'EmailSubscriptionConnection',
    }
  );

  if (!data) return <LoadingIndicator />;

  const mySubscriptions = data.currentUser?.mySubscriptions;

  const {
    items: follows = [],
    hasMore,
    cursor,
  } = extractConnectionData(mySubscriptions, src => src);

  return (
    <MySorareSubTabsLayout tabs={[]}>
      <MyPage
        toolbar={
          <>
            {sportSelectNode}
            {subscribableTypeSelectNode}
            {sortSelectNode}
          </>
        }
      >
        <Content>
          <Subscriptions subscriptions={follows} />
          <LoadMoreButton
            hasMore={hasMore}
            loading={loading}
            loadMore={() => {
              loadMore(false, {
                cursor,
                types: selectedSubscribableTypesVariables,
                sortType: sortAsVariable,
              });
            }}
          />
        </Content>
      </MyPage>
    </MySorareSubTabsLayout>
  );
};
